import type { ProgramState, Transition, TransitionType } from "./api";

// Program transitions (ADR 0081/0082). When the Director stages a new scene in
// PREVIEW and TAKEs it with AUTO / a full T-bar throw, the take carries an opaque
// `transition: {type, ms}` hint. The fan animates the program swap CLIENT-SIDE by
// freezing the outgoing frame into a snapshot layer and animating THAT layer away
// to reveal the freshly-attached new program underneath — so the existing
// attach-by-stable-key + idempotency contract (ADR 0042/0062) is never touched.
//
// This module is pure, presentation-only logic (decide + describe) so it can be
// unit-tested without the DOM; ConsumerWatch owns the <canvas> snapshot + timers.

/** Bounds for a transition duration. A hostile/garbage `ms` is clamped so a bad
 *  producer can never freeze the fan behind an outgoing snapshot. */
export const MIN_TRANSITION_MS = 100;
export const MAX_TRANSITION_MS = 4000;
export const DEFAULT_TRANSITION_MS = 500;

/** Clamp a take's transition duration into a sane range (finite → [MIN,MAX],
 *  anything else → DEFAULT). */
export function clampTransitionMs(ms: unknown): number {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return DEFAULT_TRANSITION_MS;
  return Math.min(MAX_TRANSITION_MS, Math.max(MIN_TRANSITION_MS, Math.round(ms)));
}

/** Did the on-program COMPOSITION change between two program states — i.e. is this
 *  a source/layout swap (the only kind of update a transition animates)? Overlay-,
 *  hold-, and chroma-only updates are NOT swaps and never animate. */
export function isComposedSwap(prev: ProgramState, next: ProgramState): boolean {
  return (
    (prev.source ?? "") !== (next.source ?? "") ||
    (prev.sourceB ?? "") !== (next.sourceB ?? "") ||
    (prev.layout ?? "single") !== (next.layout ?? "single")
  );
}

/**
 * Decide the transition to animate for a program update, or `null` for an instant
 * cut. Returns a transition ONLY when:
 *  - the incoming state carries a non-`cut` transition hint, AND
 *  - the composition actually changed (a real source/layout swap), AND
 *  - the previous state HAD a source on program (there is an outgoing frame to
 *    animate away — a first program / late-join has nothing to crossfade from).
 * The returned `ms` is always clamped.
 */
export function transitionForSwap(prev: ProgramState, next: ProgramState): Transition | null {
  const t = next.transition;
  if (!t || t.type === "cut") return null;
  if (!isComposedSwap(prev, next)) return null;
  // Nothing was on program before (pre-start / first take) — no outgoing frame.
  if (!prev.source) return null;
  // A swap INTO a blank program (source cleared) has nothing to reveal — just cut.
  if (!next.source && !next.sourceB) return null;
  return { type: t.type, ms: clampTransitionMs(t.ms) };
}

// The outgoing snapshot is animated away with the **Web Animations API**
// (`element.animate`), NOT a CSS transition (LSC-176). ADR 0098/0144 chased a
// CSS-transition-start race — a snapshot canvas whose `transition` property and
// animated value changed in the same React commit — with a display fix then a
// double-rAF arm; both still "jumped" on WebKit/iOS in beta, because
// changing transition-property and the animated value together is unreliable
// there. WAAPI is imperative and compositor-driven: the animation runs
// independent of React re-renders and has no transition-start race, so the
// crossfade/wipe/slide actually plays. This module stays pure (keyframes +
// options as data); ConsumerWatch calls `canvas.animate(...)` with them.

/** Keyframes for the outgoing-snapshot animation. `dissolve` crossfades (opacity),
 *  `wipe` clips the old away left→right, `slide` pushes it off to the right; `cut`
 *  (defensive) is a no-op hold. The snapshot starts covering (frame 0) and ends
 *  gone (frame 1), revealing the freshly-attached new program underneath. */
export function snapshotKeyframes(type: TransitionType): Keyframe[] {
  switch (type) {
    case "wipe":
      return [{ clipPath: "inset(0 0 0 0)" }, { clipPath: "inset(0 0 0 100%)" }];
    case "slide":
      return [{ transform: "translateX(0)" }, { transform: "translateX(100%)" }];
    case "dissolve":
      return [{ opacity: 1 }, { opacity: 0 }];
    case "cut":
    default:
      return [{ opacity: 0 }, { opacity: 0 }];
  }
}

/** WAAPI timing for a transition: the clamped duration, an easing per type, and
 *  `fill:"forwards"` so the end state (gone) holds after finish until the snapshot
 *  is torn down — no flash back to the covering frame. */
export function snapshotTiming(type: TransitionType, ms: number): KeyframeAnimationOptions {
  return {
    duration: clampTransitionMs(ms),
    easing: type === "slide" ? "cubic-bezier(0.4, 0, 0.2, 1)" : "ease-in-out",
    fill: "forwards",
  };
}
